import { z } from 'zod';
import { AvailabilityBlockSchema } from './availability.js';
/**
 * Real-user identity, device, and audit contracts for the pilot-readiness
 * tranche. Cognito will be the auth provider; these are the application-level
 * records the backend and UI care about.
 */
export const AppRoleSchema = z.enum(['inspector', 'admin', 'qa', 'support']);
export const DashboardAreaSchema = z.enum([
    'jobs',
    'availability',
    'bookings',
    'reports',
    'invoicing',
    'admin',
]);
export const DashboardSessionProfileSchema = z.object({
    userId: z.string().min(1),
    displayName: z.string().min(1),
    email: z.string().email(),
    roles: z.array(AppRoleSchema).min(1),
    inspectorId: z.string().min(1).optional(),
    /**
     * Whether the Reports page may offer "Sync Dropbox" (legacy PDF checklist
     * import). Missing on an older API means still on. The folder panel is
     * unaffected.
     */
    dropboxLegacyImportEnabled: z.boolean().default(true),
});
/** Product-level dashboard visibility. Backend routes still enforce row-level ownership. */
export function canAccessDashboardArea(roles, area) {
    if (roles.includes('admin'))
        return true;
    switch (area) {
        case 'jobs':
        case 'bookings':
            return roles.some((role) => ['inspector', 'qa', 'support'].includes(role));
        case 'availability':
            return roles.some((role) => ['inspector', 'support'].includes(role));
        case 'reports':
            return roles.some((role) => ['inspector', 'qa'].includes(role));
        case 'invoicing':
        case 'admin':
            return false;
    }
}
export const UserStatusSchema = z.enum(['active', 'disabled']);
export const AppUserSchema = z.object({
    userId: z.string().min(1),
    email: z.string().trim().email(),
    displayName: z.string().trim().min(1),
    legalName: z.string().trim().min(1).optional(),
    mobile: z.string().trim().min(1).optional(),
    cerRegistrationNumber: z.string().trim().min(1).optional(),
    roles: z.array(AppRoleSchema).min(1),
    inspectorId: z.string().trim().min(1).optional(),
    serviceM8StaffUuid: z.string().uuid().optional(),
    serviceM8StaffLinks: z
        .object({
        actnow: z.string().uuid().optional(),
        leadingEdge: z.string().uuid().optional(),
    })
        .optional(),
    availabilityBlocks: z.array(AvailabilityBlockSchema).optional(),
    status: UserStatusSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AppUserListFilterSchema = z.object({
    status: UserStatusSchema.optional(),
    role: AppRoleSchema.optional(),
    limit: z.coerce.number().int().positive().max(500).default(200),
});
export const UpsertAppUserSchema = z.object({
    email: z.string().trim().email(),
    displayName: z.string().trim().min(1),
    legalName: z.string().trim().min(1).optional(),
    mobile: z.string().trim().min(1).optional(),
    cerRegistrationNumber: z.string().trim().min(1).optional(),
    roles: z.array(AppRoleSchema).min(1),
    inspectorId: z.string().trim().min(1).optional(),
    serviceM8StaffUuid: z.string().uuid().optional(),
    serviceM8StaffLinks: z
        .object({ actnow: z.string().uuid().optional(), leadingEdge: z.string().uuid().optional() })
        .optional(),
    status: UserStatusSchema.default('active'),
});
export const UpdateAppUserSchema = z.object({
    email: z.string().trim().email().optional(),
    displayName: z.string().trim().min(1).optional(),
    legalName: z.string().trim().min(1).nullable().optional(),
    mobile: z.string().trim().min(1).nullable().optional(),
    cerRegistrationNumber: z.string().trim().min(1).nullable().optional(),
    roles: z.array(AppRoleSchema).min(1).optional(),
    inspectorId: z.string().trim().min(1).nullable().optional(),
    serviceM8StaffUuid: z.string().uuid().nullable().optional(),
    serviceM8StaffLinks: z
        .object({
        actnow: z.string().uuid().nullable().optional(),
        leadingEdge: z.string().uuid().nullable().optional(),
    })
        .optional(),
    status: UserStatusSchema.optional(),
});
export const InspectorSeedRowSchema = z.object({
    displayName: z.string().trim().min(1),
    legalName: z.string().trim().min(1),
    email: z.string().trim().email(),
    mobile: z.string().trim().optional().default(''),
    cerRegistrationNumber: z.string().trim().min(1),
    serviceM8Tenancy: z.enum(['ACTNOW', 'Leading Edge']),
    serviceM8StaffName: z.string().trim().min(1),
    active: z.boolean().default(true),
});
export const InspectorSeedImportSchema = z.object({
    rows: z.array(InspectorSeedRowSchema).min(1).max(500),
});
export const InspectorServiceM8DirectorySchema = z.object({
    inspectors: z.array(AppUserSchema),
    tenancies: z.array(z.object({
        id: z.enum(['actnow', 'leadingEdge']),
        name: z.string(),
        available: z.boolean(),
        staff: z.array(z.object({
            uuid: z.string().uuid(),
            name: z.string(),
            email: z.string().optional(),
            mobile: z.string().optional(),
            active: z.boolean(),
        })),
        error: z.string().optional(),
    })),
});
export const RegisteredDeviceSchema = z.object({
    deviceId: z.string().uuid(),
    userId: z.string().min(1),
    label: z.string().trim().min(1),
    createdAt: z.string(),
    lastSeenAt: z.string(),
    appVersion: z.string().optional(),
    revokedAt: z.string().optional(),
});
export const AuditActorTypeSchema = z.enum(['user', 'device', 'system', 'consent-link']);
export const AuditEventSchema = z.object({
    eventId: z.string().uuid(),
    jobId: z.string().min(1).optional(),
    actorType: AuditActorTypeSchema,
    actorId: z.string().min(1),
    action: z.string().min(1),
    occurredAt: z.string(),
    correlationId: z.string().min(1),
    summary: z.string().min(1),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export function isActiveUser(user) {
    return user.status === 'active';
}
export function hasAnyRole(user, roles) {
    return isActiveUser(user) && roles.some((role) => user.roles.includes(role));
}
export function isDeviceRevoked(device) {
    return Boolean(device.revokedAt);
}
//# sourceMappingURL=identity.js.map